import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { installAuthInterceptor } from './utils/authTokens';

// Install the global axios interceptor for silent access-token refresh on 401.
installAuthInterceptor();

// reCAPTCHA v3 Site Key - loaded from environment variable with fallback
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LfXOlAsAAAAAEOvaxVVGGM-YzgqyDLvwtJf8yLG';

// Common Components
import CookieConsent from './components/common/CookieConsent';

// Landing Page Components
import Navbar from './components/landing/Navbar';
import HeroSection from './components/landing/HeroSection';
import FeaturesSection from './components/landing/FeaturesSection';
import HowItWorksSection from './components/landing/HowItWorksSection';
import Footer from './components/landing/Footer';
import LoginModal from './components/landing/LoginModal';
import ContactModal from './components/landing/ContactModal';
import FAQModal from './components/landing/FAQModal';
import ClientPortalModal from './components/landing/ClientPortalModal';

// Admin Components
import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import Partners from './pages/admin/Partners';
import Settings from './pages/admin/Settings';
import AdminVideoSettings from './pages/admin/AdminVideoSettings';
import WillParameters from './pages/admin/WillParameters';
import EmbedSettings from './pages/admin/EmbedSettings';
import AdminFAQs from './pages/admin/AdminFAQs';
import AdminLegal from './pages/admin/AdminLegal';
import AdminConsentLogs from './pages/admin/AdminConsentLogs';
import ClientFeatures from './pages/admin/ClientFeatures';
import LoginFeatures from './pages/admin/LoginFeatures';
import PartnerLedgers from './pages/admin/PartnerLedgers';
import AdminBackup from './pages/admin/AdminBackup';
import Integrations from './pages/admin/Integrations';
import AdminDiagnostics from './pages/admin/AdminDiagnostics';
import AdminLoginPopup from './pages/admin/AdminLoginPopup';
import KnowledgePreview from './pages/KnowledgePreview';
import Knowledge from './pages/Knowledge';
import KnowledgeArticle from './pages/KnowledgeArticle';
import AdminKnowledge from './pages/admin/AdminKnowledge';
import AdminKnowledgeEditor from './pages/admin/AdminKnowledgeEditor';

// Partner Components
import PartnerLayout from './components/partner/PartnerLayout';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerProfile from './pages/partner/PartnerProfile';
import PartnerBilling from './pages/partner/PartnerBilling';
import PartnerSettings from './pages/partner/PartnerSettings';
import PartnerResetPassword from './pages/partner/PartnerResetPassword';
import PartnerMFBrokerage from './pages/partner/PartnerMFBrokerage';
import PartnerEmailTemplates from './pages/partner/PartnerEmailTemplates';
import PartnerLedger from './pages/partner/PartnerLedger';
import PartnerConsentHistory from './pages/partner/PartnerConsentHistory';

// Partner Will Components
import PartnerWillManagement from './pages/partner/PartnerWillManagement';
import PartnerWillCreation from './components/will/PartnerWillCreation';

// Services
import AdminServices from './pages/admin/AdminServices';
import PartnerServices from './pages/partner/PartnerServices';

// Client Components
import ClientLogin from './pages/client/ClientLogin';
import ClientLayout from './components/client/ClientLayout';
import ClientWill from './pages/client/ClientWill';
import ClientLifeVault from './pages/client/ClientLifeVault';
import ClientProfile from './pages/client/ClientProfile';
import ClientSettings from './pages/client/ClientSettings';
import ClientResetPassword from './pages/client/ClientResetPassword';
import ClientInvoices from './pages/client/ClientInvoices';
import ClientConsentHistory from './pages/client/ClientConsentHistory';
import WillCreation from './components/will/WillCreation';
import PaymentSuccess from './pages/client/PaymentSuccess';

// Embed Components
import EmbedClientPortal from './pages/embed/EmbedClientPortal';

// Standalone Pages
import VerifyEmail from './pages/VerifyEmail';
import AboutUs from './pages/public/AboutUs';

// Admin About Us
import AdminAboutUs from './pages/admin/AdminAboutUs';

// Trusted Contact Portal Components
import TrustedLogin from './pages/trusted/TrustedLogin';
import TrustedDashboard from './pages/trusted/TrustedDashboard';

// Version Badge Component


// Branding (logo / favicon sync)
import { useBranding, applyFavicon } from './hooks/useBranding';

// Main domains - subdomains of these will trigger client portal
const MAIN_DOMAINS = ['willkart.ai', 'willkart.in'];

/**
 * Detect subdomain from hostname
 * Returns subdomain string or null
 */
const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Test/preview-only: allow forcing a subdomain via ?subdomain=xxx query param.
  // Strictly gated to preview/localhost hosts so production cannot be spoofed via URL.
  const isPreviewHost = hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.includes('preview.emergentagent.com')
    || hostname.includes('preview.emergent');
  if (isPreviewHost) {
    try {
      const params = new URLSearchParams(window.location.search);
      const forced = params.get('subdomain');
      if (forced) {
        try { localStorage.setItem('__forced_subdomain', forced); } catch (_) {}
        return forced;
      }
      const cached = (typeof localStorage !== 'undefined') ? localStorage.getItem('__forced_subdomain') : null;
      if (cached) return cached;
    } catch (_) { /* ignore */ }
  }

  // Skip for localhost/preview
  if (isPreviewHost) {
    return null;
  }
  
  for (const mainDomain of MAIN_DOMAINS) {
    if (hostname.endsWith(`.${mainDomain}`) && hostname !== `www.${mainDomain}`) {
      const subdomain = hostname.replace(`.${mainDomain}`, '');
      if (subdomain && subdomain !== 'www') {
        console.log(`[WillKart] Detected subdomain: ${subdomain}`);
        return subdomain;
      }
    }
  }
  
  return null;
};

// Get subdomain once on load
const DETECTED_SUBDOMAIN = getSubdomain();

const LandingPage = () => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [loginInitialStep, setLoginInitialStep] = useState('email');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [isClientPortalOpen, setIsClientPortalOpen] = useState(false);

  // Open the LoginModal at the audience picker (used by ambiguous "Get Started" CTAs)
  const openLoginWithAudience = () => {
    setLoginInitialStep('audience');
    setIsLoginOpen(true);
  };

  // Open the LoginModal directly at the partner email step (used by "Partner Login" navbar button)
  const openPartnerLogin = () => {
    setLoginInitialStep('email');
    setIsLoginOpen(true);
  };

  // Handle hash-based scrolling on initial load (e.g. arriving at /#how-it-works)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    // Wait for sections to render before scrolling
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar 
        onLoginClick={openPartnerLogin} 
        onContactClick={() => setIsContactOpen(true)}
        onFAQClick={() => setIsFAQOpen(true)}
        onClientPortalClick={() => setIsClientPortalOpen(true)}
      />
      <main>
        <HeroSection onGetStarted={openLoginWithAudience} />
        <FeaturesSection />
        <HowItWorksSection />
      </main>
      <Footer onContactClick={() => setIsContactOpen(true)} />
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} initialStep={loginInitialStep} />
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <FAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
      <ClientPortalModal isOpen={isClientPortalOpen} onClose={() => setIsClientPortalOpen(false)} />
      <Toaster position="top-right" richColors />
    </div>
  );
};

// Routes for main domain (willkart.ai)
const MainRoutes = () => (
  <Routes>
    <Route path="/" element={<LandingPage />} />
    <Route path="/about" element={<AboutUs />} />
    <Route path="/knowledge" element={<Knowledge />} />
    <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
    <Route path="/knowledge-preview" element={<KnowledgePreview />} />

    {/* Admin Routes */}
    <Route path="/admin" element={<AdminLayout />}>
      <Route path="login" element={<AdminLogin />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="partners" element={<Partners />} />
      <Route path="partner-ledgers" element={<PartnerLedgers />} />
      <Route path="will-parameters" element={<WillParameters />} />
      <Route path="client-features" element={<ClientFeatures />} />
      <Route path="login-features" element={<LoginFeatures />} />
      <Route path="login-popup" element={<AdminLoginPopup />} />
      <Route path="about-us" element={<AdminAboutUs />} />
      <Route path="embed-settings" element={<EmbedSettings />} />
      <Route path="videos" element={<AdminVideoSettings />} />
      <Route path="faqs" element={<AdminFAQs />} />
      <Route path="knowledge" element={<AdminKnowledge />} />
      <Route path="knowledge/new" element={<AdminKnowledgeEditor />} />
      <Route path="knowledge/edit/:id" element={<AdminKnowledgeEditor />} />
      <Route path="legal" element={<AdminLegal />} />
      <Route path="consent-logs" element={<AdminConsentLogs />} />
      <Route path="backup" element={<AdminBackup />} />
      <Route path="integrations" element={<Integrations />} />
      <Route path="diagnostics" element={<AdminDiagnostics />} />
      <Route path="services" element={<AdminServices />} />
      <Route path="settings" element={<Settings />} />
    </Route>

    {/* Partner Routes */}
    <Route path="/partner" element={<PartnerLayout />}>
      <Route path="dashboard" element={<PartnerDashboard />} />
      <Route path="wills" element={<PartnerWillManagement />} />
      <Route path="will/create/:willId" element={<PartnerWillCreation />} />
      <Route path="services" element={<PartnerServices />} />
      <Route path="ledger" element={<PartnerLedger />} />
      <Route path="mf-brokerage" element={<PartnerMFBrokerage />} />
      <Route path="email-templates" element={<PartnerEmailTemplates />} />
      <Route path="consent-history" element={<PartnerConsentHistory />} />
      <Route path="profile" element={<PartnerProfile />} />
      <Route path="billing" element={<PartnerBilling />} />
      <Route path="settings" element={<PartnerSettings />} />
    </Route>
    
    {/* Partner Password Reset (outside layout) */}
    <Route path="/partner/reset-password" element={<PartnerResetPassword />} />
    
    {/* Email Verification (for walk-in partners and clients) */}
    <Route path="/verify-email" element={<VerifyEmail />} />

    {/* Payment Success Callback */}
    <Route path="/payment/success" element={<PaymentSuccess />} />

    {/* Trusted Contact Routes (path-based for testing) */}
    <Route path="/trusted/login" element={<TrustedLogin />} />
    <Route path="/trusted/setup" element={<TrustedLogin />} />
    <Route path="/trusted/reset-password" element={<TrustedLogin />} />
    <Route path="/trusted/dashboard" element={<TrustedDashboard />} />
    <Route path="/trusted" element={<Navigate to="/trusted/login" replace />} />

    {/* Embed Routes - Partner iframe embedding */}
    {/* EmbedClientPortal handles auth, then uses Outlet for ClientLayout which handles nested routes */}
    <Route path="/embed/:partnerId" element={<EmbedClientPortal />}>
      <Route path="client" element={null} />
      <Route path="dashboard" element={<ClientWill />} />
      <Route path="will" element={<ClientWill />} />
      <Route path="will/create/:willId" element={<WillCreation />} />
      <Route path="life-vault" element={<ClientLifeVault />} />
      <Route path="invoices" element={<ClientInvoices />} />
      <Route path="consent-history" element={<ClientConsentHistory />} />
      <Route path="profile" element={<ClientProfile />} />
      <Route path="settings" element={<ClientSettings />} />
    </Route>
  </Routes>
);

// Routes for subdomains (oth.willkart.ai)
const SubdomainRoutes = ({ subdomain }) => (
  <Routes>
    {/* Root and login go to ClientLogin */}
    <Route path="/" element={<ClientLogin subdomainFromHost={subdomain} />} />
    <Route path="/login" element={<ClientLogin subdomainFromHost={subdomain} />} />
    
    {/* Password reset route */}
    <Route path="/reset-password" element={<ClientResetPassword />} />

    {/* Payment Success Callback */}
    <Route path="/payment/success" element={<PaymentSuccess />} />
    
    {/* Trusted Contact Portal Routes */}
    <Route path="/trusted/login" element={<TrustedLogin subdomainFromHost={subdomain} />} />
    <Route path="/trusted/setup" element={<TrustedLogin subdomainFromHost={subdomain} />} />
    <Route path="/trusted/reset-password" element={<TrustedLogin subdomainFromHost={subdomain} />} />
    <Route path="/trusted/dashboard" element={<TrustedDashboard />} />
    <Route path="/trusted" element={<Navigate to="/trusted/login" replace />} />
    
    {/* Client dashboard routes - using explicit paths for better matching */}
    <Route path="/dashboard" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientWill />} />
    </Route>
    <Route path="/will" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientWill />} />
      <Route path="create/:willId" element={<WillCreation />} />
    </Route>
    <Route path="/life-vault" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientLifeVault />} />
    </Route>
    <Route path="/invoices" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientInvoices />} />
    </Route>
    <Route path="/consent-history" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientConsentHistory />} />
    </Route>
    <Route path="/profile" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientProfile />} />
    </Route>
    <Route path="/settings" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route index element={<ClientSettings />} />
    </Route>
    
    {/* Catch-all redirect to dashboard */}
    <Route path="*" element={<ClientLayout subdomainFromHost={subdomain} />}>
      <Route path="*" element={<ClientWill />} />
    </Route>
  </Routes>
);

function App() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      <div className="App">
        <BrowserRouter>
          <FaviconSync />
          {DETECTED_SUBDOMAIN ? (
            <SubdomainRoutes subdomain={DETECTED_SUBDOMAIN} />
          ) : (
            <MainRoutes />
          )}
          <Toaster position="top-right" richColors />
          <CookieConsent />
        </BrowserRouter>
      </div>
    </GoogleReCaptchaProvider>
  );
}

// Keeps <link rel="icon"> in sync with admin-uploaded logo_small
const FaviconSync = () => {
  const { logoSmall, logoMain } = useBranding();
  useEffect(() => {
    const favicon = logoSmall || logoMain;
    if (favicon) applyFavicon(favicon);
  }, [logoSmall, logoMain]);
  return null;
};

export default App;
